import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { useWishlistStore } from "@/store/wishlistStore";
import { supabase } from "@/integrations/supabase/client";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { WishlistLoginPrompt } from "@/components/WishlistLoginPrompt";
import { ScrollToTop } from "@/hooks/useScrollToTop";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { CookieBanner, CookiePreferencesModal } from "@/components/CookieConsent";
import { LanguageProvider } from "@/i18n";
import { AlertToastProvider } from "@/contexts/AlertToastContext";
import { AlertToastSync } from "@/components/AlertToastSync";
import { JewelryAdvisorBot } from "@/components/JewelryAdvisor";
import { useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Shop from "./pages/Shop";
import Category from "./pages/Category";
import Product from "./pages/Product";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import Wishlist from "./pages/Wishlist";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Returns from "./pages/Returns";
import Accessibility from "./pages/Accessibility";
import TradeIn from "./pages/TradeIn";
import RingSizeGuide from "./pages/RingSizeGuide";
import Privacy from "./pages/Privacy";
import Shipping from "./pages/Shipping";
import FAQ from "./pages/FAQ";
import GiftCard from "./pages/GiftCard";
import CookiePolicy from "./pages/CookiePolicy";
import AdminContactMessages from "./pages/AdminContactMessages";
import AdminRepairs from "./pages/AdminRepairs";
import TrackOrder from "./pages/TrackOrder";
import AboutUs from "./pages/AboutUs";
import Cart from "./pages/Cart";
import NotFound from "./pages/NotFound";
import Sitemap from "./pages/Sitemap";
import TVDisplay from "./pages/TVDisplay";

const queryClient = new QueryClient();

const AppContent = () => {
  const loadWishlist = useWishlistStore((state) => state.loadWishlist);
  const preload = useGlobalPreload((state) => state.preload);
  const location = useLocation();
  const isTVPage = location.pathname === "/tv";

  useEffect(() => {
    preload();
  }, [preload]);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        loadWishlist();
      }
    };

    loadUserData();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        loadWishlist();
      } else if (event === 'SIGNED_OUT') {
        loadWishlist();
      }
    });

    return () => subscription.unsubscribe();
  }, [loadWishlist]);

  return (
    <>
      <ScrollToTop />
      {!isTVPage && <WishlistLoginPrompt />}
      {!isTVPage && <CookieBanner />}
      {!isTVPage && <CookiePreferencesModal />}
      {!isTVPage && <JewelryAdvisorBot />}
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/collections/:category" element={<Category />} />
        {/* Shopify-compatible product URLs: /collections/:collection/products/:handle */}
        <Route path="/collections/:collection/products/:handle" element={<Product />} />
        {/* Fallback for direct product URLs */}
        <Route path="/products/:handle" element={<Product />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/returns" element={<Returns />} />
        <Route path="/accessibility" element={<Accessibility />} />
        <Route path="/trade-in" element={<TradeIn />} />
        <Route path="/ring-size-guide" element={<RingSizeGuide />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/shipping" element={<Shipping />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/gift-card" element={<GiftCard />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
        <Route path="/admin/repairs" element={<AdminRepairs />} />
        <Route path="/track-order" element={<TrackOrder />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/sitemap.xml" element={<Sitemap />} />
        <Route path="/tv" element={<TVDisplay />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <CookieConsentProvider>
            <AlertToastProvider>
              <AlertToastSync />
              <Toaster />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </AlertToastProvider>
          </CookieConsentProvider>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
